import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { resolveImage } from "@/lib/api";
import { useBrands } from "@/components/Yachts";
import { BrandLogo } from "@/components/Brands";

const BrandTile = ({ brand, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.9, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
    className=""
  >
    <Link to={`/yachts/${brand.slug}`} className="group block" data-testid={`brand-tile-${brand.slug}`}>
      <div className="relative overflow-hidden border border-navy-ink/10">
        <img
          src={resolveImage(brand.hero_image)}
          alt={brand.name}
          className="aspect-[4/3] w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
        <span className="absolute left-6 top-6 text-[10px] uppercase tracking-[0.35em] text-white/80">
          {String(index + 1).padStart(2, "0")} — {brand.origin}
        </span>
        <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between gap-4">
          <div>
            <h3 className="font-serif text-2xl font-light text-white sm:text-3xl" data-testid={`brand-tile-${brand.slug}-name`}>{brand.name}</h3>
            <p className="mt-2 text-xs font-light text-white/70">{brand.tagline}</p>
          </div>
          <span className="shrink-0 rounded-full border border-white/40 bg-black/25 px-3 py-1.5 text-[9px] uppercase tracking-[0.3em] text-white backdrop-blur-sm transition-colors duration-300 group-hover:border-gold-pale group-hover:text-gold-pale" data-testid={`brand-tile-${brand.slug}-count`}>
            {brand.model_count} {brand.model_count === 1 ? "Model" : "Models"}
          </span>
        </div>
      </div>
      <div className="flex items-center justify-between gap-4 border-b border-navy-ink/10 py-5">
        <div className="flex h-9 max-w-[55%] items-center overflow-hidden">
          <BrandLogo brand={brand} className="max-h-7 max-w-full object-contain" />
        </div>
        <span className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap text-[11px] uppercase tracking-[0.3em] text-gold">
          View the range <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  </motion.div>
);

export default function YachtsPage() {
  const { data: brands = [], isLoading } = useBrands();

  return (
    <section className="relative bg-navy px-6 pb-24 pt-36 lg:px-12 lg:pb-36 lg:pt-48" data-testid="yachts-page">
      <div className="mx-auto max-w-7xl">
        <div className="mb-20 flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-6 text-[10px] uppercase tracking-[0.45em] text-gold sm:text-xs"
              data-testid="yachts-page-overline"
            >
              New Yachts — Select a Marque
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.1 }}
              className="max-w-3xl font-serif text-4xl font-light leading-[1.02] tracking-tight text-navy-ink sm:text-5xl lg:text-6xl"
              data-testid="yachts-page-heading"
            >
              {["", "One marque.", "Two marques.", "Three marques.", "Four marques.", "Five marques.", "Six marques."][brands.length] || `${brands.length} marques.`}
              <span className="italic text-gold-light"> One standard.</span>
            </motion.h1>
          </div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.3 }}
            className="max-w-sm text-sm font-light leading-relaxed text-mist"
            data-testid="yachts-page-description"
          >
            Choose a marque to explore its current new-build range. Every model
            can be specified, sea-trialled and delivered anywhere on the Indian coastline.
          </motion.p>
        </div>

        {isLoading && <p className="text-sm text-mist" data-testid="yachts-page-loading">Loading marques…</p>}

        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 lg:gap-x-8">
          {brands.map((b, i) => (
            <BrandTile key={b.slug} brand={b} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
